import { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import type { SendContactOption } from '@jobos/shared';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * The contact menu beside the send dialog's "To" field (NEW-239).
 *
 * A COMPANION to the chip input, never a replacement for it. The recipient
 * field has always accepted any typed address and any number of them, and a
 * select-one dropdown in its place would take both away to solve a lookup
 * problem. This only answers "what addresses does this client have", which is
 * the thing staff were leaving the app to find out.
 *
 * Every row TOGGLES. A ticked row that did nothing when clicked would be the
 * one affordance in the dialog that lies about being interactive, and removing
 * an address you just added should not mean hunting for its chip's ×.
 *
 * Addresses are compared case-insensitively, exactly as EmailChipInput's own
 * duplicate check does — `Jane@c.com` picked from here must not become a second
 * chip beside a typed `jane@c.com`.
 */
export interface RecipientPickerProps {
  /** Every address on file for the client, from the send preview. */
  options: SendContactOption[];
  /** The addresses currently in the "To" field. */
  selected: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}

/** Above this many rows the menu is worth filtering; below it, the box is noise. */
const SEARCH_THRESHOLD = 6;

export function RecipientPicker({
  options,
  selected,
  onChange,
  disabled = false,
}: RecipientPickerProps) {
  const [open, setOpen] = useState(false);

  const selectedKeys = new Set(selected.map((email) => email.trim().toLowerCase()));

  const toggle = (email: string) => {
    const key = email.toLowerCase();
    if (selectedKeys.has(key)) {
      onChange(selected.filter((existing) => existing.trim().toLowerCase() !== key));
      return;
    }
    onChange([...selected, email]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          disabled={disabled}
          aria-label="Choose from client contacts"
          className="mt-0.5 shrink-0"
        >
          <ChevronDown className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        {options.length === 0 ? (
          /* A note, not an empty menu — "nothing here" and "this is broken"
             look identical otherwise. The trigger itself stays enabled so the
             control does not silently disappear on a client with no contacts. */
          <p className="px-3 py-4 text-sm text-gray-500">
            No contacts on file for this client. Type an address instead.
          </p>
        ) : (
          <Command>
            {options.length > SEARCH_THRESHOLD && <CommandInput placeholder="Search contacts…" />}
            <CommandList>
              <CommandEmpty>No matching contact.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => {
                  const picked = selectedKeys.has(option.email.toLowerCase());
                  return (
                    <CommandItem
                      key={option.email}
                      // cmdk filters on this, so both halves of the row are
                      // searchable rather than just whichever one it defaults to.
                      value={`${option.label} ${option.email}`}
                      onSelect={() => toggle(option.email)}
                      className="flex items-start gap-2"
                    >
                      <Check
                        className={cn(
                          'mt-0.5 h-4 w-4 shrink-0',
                          picked ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <span className="min-w-0">
                        <span className="block truncate">{option.label}</span>
                        <span className="block truncate text-xs text-gray-500">{option.email}</span>
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
}
